import React, { PropsWithChildren, useEffect, useState } from 'react';

import { FlexColumn, FlexRow } from '@editor/components/utilities/layouts';
import { useRevampedCheckoutFlowVariant } from '@editor/global/growthbookUtils';
import {
  promotionSelector,
  usePromotionStore,
} from '@editor/stores/promotionStore';
import { useSharedConfigStore } from '@editor/stores/sharedConfigStore';
import { getWorkspaceMe } from '@repo/api-client';
import { isMobileOperatingSystem } from '@repo/detection';
import { BillingAdminAccess } from '@repo/subscription/client';
import { Badge } from '@repo/ui/components/Badge';
import { Button, secondaryTheme } from '@repo/ui/components/Buttons';
import PremiumBadgeIcon from '@repo/ui/components/Icon/PremiumBadgeIcon';
import PricingCheckIcon from '@repo/ui/components/Icon/PricingCheckIcon';
import CheckIcon from '@repo/ui/components/Icon/v2/CheckIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { H2, H3, H5, P2, P3 } from '@repo/ui/components/Typography';
import { themeStyle as themeStyleLegacy } from '@repo/ui/theming';
import IconInfoCircle from '@repo/ui-icons/IconInfoCircle';
import { themeStyle } from '@repo/ui-kit/themeStyle';

import { FREE_TRIAL_DAYS_AMOUNT } from '../../global/constants';
import useFreeTrial from '../../hooks/useFreeTrial';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '../../stores/planSelectionStore';
import { CheckoutFlowVariants } from '../Subscription/SubscriptionModal/VariantFlows/types';
import PanelHeader from '../utilities/PanelHeader/PanelHeader';

import {
  getConfig,
  getFreeTrialConfig,
  getBackground,
  imageComponents,
} from './config';
import {
  ContentWrapper,
  MobileContentWrapper,
  ImageComponentWrapper,
  ImageMobileContainer,
  ImageContainer,
  ImageHideContainer,
  Wrapper,
  FreeTrialImageComponent,
  MobileImageComponentWrapper,
  UpgradeNowContainer,
} from './styles';
import {
  ImageComponentType,
  UpgradeModalContentType,
  UpgradeModalIncentiveContentBuilderProps,
  UpgradeModalIncentiveContentProps,
  type UpgradeModalConfigData,
} from './types';

/**
 * From the design we need to show special image for upgrade modals if the free trial is available.
 * Otherwise we need to show the default image which may differ depend on the upgrade type.
 * We should not update the `getBackground` function with the `FreeTrial.png` as we show the default image for the free trial
 * opened from the pricing page (see the freeTrial section in the config).
 */
const getModifiedConfigData = (
  data: UpgradeModalIncentiveContentBuilderProps,
  isFreeTrialAvailable: boolean,
  contentType: UpgradeModalContentType,
  isMobile?: boolean
): UpgradeModalIncentiveContentBuilderProps => {
  if (!isFreeTrialAvailable) {
    return data;
  }

  if (contentType === 'freeTrial') {
    return { ...data, dataTestId: data.dataTestId + ' upgrade-to-free-trial' };
  }

  const freeTrialData = getFreeTrialConfig();
  const result: UpgradeModalIncentiveContentBuilderProps = {
    ...data,
    dataTestId: data.dataTestId + ' free-trial',
    description: `${
      data.description ?? ''
    } Start with a ${FREE_TRIAL_DAYS_AMOUNT}-day free trial.`.trim(),
    tag: freeTrialData.tag,
    buttonLabel: freeTrialData.buttonLabel,
    upgradeButtonDescription: freeTrialData.upgradeButtonDescription,
    imageComponentType: undefined, // we want to explicitly have this as undefined to ensure that no stale value exists
    imageComponent: (
      <FreeTrialImageComponent data-theme="dark">
        {isMobile ? (
          <Spacer height="16px" />
        ) : (
          <>
            <H2>{`${FREE_TRIAL_DAYS_AMOUNT}-Day Free Trial`}</H2>
            <Spacer height="32px" />
          </>
        )}
        {freeTrialData.content}
      </FreeTrialImageComponent>
    ),
    background: 'url("/images/upgrade/FreeTrial.png")',
  };
  return result;
};

type Props = {
  title?: string;
  description?: string;
  dataTestId?: string;
  hideImageOnMobile: boolean;
  background: string;
  imageComponent: React.ReactNode;
  imageComponentType?: ImageComponentType;
};

const RevampedCheckoutFlowMobileWrapper: React.FC<PropsWithChildren<Props>> = ({
  children,
  title,
  description,
  dataTestId,
  background,
  imageComponentType,
  imageComponent,
}) => {
  const showImageComponent =
    imageComponentType !== 'brands' && imageComponentType !== 'aiImages'; // we want this to be explicitly hidden

  return (
    <FlexColumn gap="16px">
      <H3>{title}</H3>
      <Wrapper data-testid={dataTestId} gap="16px">
        {showImageComponent && (
          <ImageMobileContainer background={background}>
            <MobileImageComponentWrapper>
              {imageComponent}
            </MobileImageComponentWrapper>
          </ImageMobileContainer>
        )}
        <P2>{description}</P2>
        <MobileContentWrapper>{children}</MobileContentWrapper>
      </Wrapper>
    </FlexColumn>
  );
};

const OldCheckoutFlowWrapper: React.FC<PropsWithChildren<Props>> = ({
  children,
  dataTestId,
  hideImageOnMobile,
  background,
  imageComponent,
}) => {
  const ImageWrapper = hideImageOnMobile ? ImageHideContainer : ImageContainer;

  return (
    <Wrapper
      data-testid={dataTestId}
      style={{ flexWrap: hideImageOnMobile ? 'unset' : 'wrap' }}
    >
      <ContentWrapper>{children}</ContentWrapper>
      <ImageWrapper background={background}>
        <ImageComponentWrapper>{imageComponent}</ImageComponentWrapper>
      </ImageWrapper>
    </Wrapper>
  );
};

const UpgradeModalIncentiveContentBuilder: React.FC<
  UpgradeModalIncentiveContentBuilderProps
> = (props) => {
  const {
    title,
    tag,
    description,
    items,
    content,
    background,
    imageComponent,
    imageComponentType,
    onUpgrade,
    hideImageOnMobile,
    buttonLabel,
    upgradeButtonDescription,
    hideButtonDescription,
    secondaryButtonLabel,
    onSecondaryButtonClick,
    dataTestId,
    onlyExpert,
    itemsTitle,
    headerText,
  } = props;

  const { setUpgrade, setUpgradeToExpertPlan } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );
  const { onClose } = usePromotionStore(promotionSelector.upgradeModalProps);

  const handleUpgrade = (): void => {
    if (onlyExpert) {
      /**
       * We want to upgrade directly to expert plan from free plan if user
       * wants to upgrade for brand kits feature.
       * Pro subscription plan doesn't support brand kits entirely.
       * */
      setUpgradeToExpertPlan(true);
    } else {
      setUpgrade(true);
    }
    onUpgrade();
  };

  const handleClose = (): void => {
    setUpgrade(false);
    onClose();
  };

  // Revamped checkout flow
  const showRevampedCheckoutFlow =
    useRevampedCheckoutFlowVariant() !== CheckoutFlowVariants.Off;
  const shouldShowRevampedMobileCheckoutFlow =
    showRevampedCheckoutFlow && isMobileOperatingSystem();

  const Icon = showRevampedCheckoutFlow ? CheckIcon : PricingCheckIcon;

  const ModalContentWrapper = shouldShowRevampedMobileCheckoutFlow
    ? RevampedCheckoutFlowMobileWrapper
    : OldCheckoutFlowWrapper;

  let tagContent = null;

  if (tag) {
    tagContent = <Badge text={tag} variant="blue" />;
  }

  return (
    <ModalContentWrapper
      background={background}
      dataTestId={dataTestId}
      description={description}
      hideImageOnMobile={hideImageOnMobile}
      imageComponent={imageComponent}
      imageComponentType={imageComponentType}
      title={title}
    >
      {!shouldShowRevampedMobileCheckoutFlow && (
        <>
          <FlexColumn>
            <PanelHeader
              customElement={
                <FlexRow alignItems="center" gap="8px">
                  <PremiumBadgeIcon height="20px" />
                  <H5>{headerText ?? 'Upgrade Plan'}</H5>
                  {tagContent}
                </FlexRow>
              }
              hideCloseIcon={!isMobileOperatingSystem()}
              onClose={handleClose}
            />

            <Spacer height="24px" />
            <H2>{title}</H2>
            <Spacer height="12px" />
            <P2>{description}</P2>
          </FlexColumn>
          <Spacer height="32px" />
        </>
      )}
      {items && items.length > 0 && (
        <>
          <H5>{itemsTitle ?? "Here is what's included:"}</H5>
          <Spacer
            height={shouldShowRevampedMobileCheckoutFlow ? '16px' : '14px'}
          />
          <FlexColumn
            alignItems="center"
            gap={shouldShowRevampedMobileCheckoutFlow ? '16px' : '24px'}
          >
            {items.map((item, index) => (
              <FlexRow
                key={`item-${index}`}
                alignItems={showRevampedCheckoutFlow ? 'center' : 'flex-start'}
                gap="12px"
                minWidth="100%"
              >
                <Icon
                  height="22px"
                  theme={{ color: themeStyleLegacy.varBlueMedium }}
                />
                {item}
              </FlexRow>
            ))}
          </FlexColumn>
          <Spacer
            height={shouldShowRevampedMobileCheckoutFlow ? '16px' : '24px'}
          />
        </>
      )}
      {content && (
        <>
          {content}
          <Spacer
            height={shouldShowRevampedMobileCheckoutFlow ? '16px' : '24px'}
          />
        </>
      )}
      <UpgradeNowContainer alignItems="center" width="100%">
        <BillingAdminAccess
          fallback={
            <FlexRow
              alignItems="flex-start"
              gap={themeStyle.spacing8}
              padding={themeStyle.spacing12}
              style={{
                backgroundColor: themeStyle.colorBgFillSecondaryActive,
                borderRadius: themeStyle.borderRadius12,
              }}
            >
              <IconInfoCircle color={themeStyle.colorIcon} size={16} />
              <P3>
                Ask the owner of your team to upgrade the plan in order to
                increase the limits and access the premium features.
              </P3>
            </FlexRow>
          }
        >
          <Button
            dataTestId="upgradeModalIncentiveContent--upgrade-modal-button"
            height="40px"
            label={buttonLabel || 'Upgrade Now'}
            name="upgradeNowButton"
            width="100%"
            onClick={handleUpgrade}
          />
          <Spacer height="8px" />
          {secondaryButtonLabel && onSecondaryButtonClick && (
            <>
              <Button
                height="40px"
                label={secondaryButtonLabel}
                name="upgradeModalSecondaryButton"
                theme={secondaryTheme}
                width="100%"
                onClick={onSecondaryButtonClick}
              />
              <Spacer height="8px" />
            </>
          )}
          {!hideButtonDescription && (
            <P3 style={{ textAlign: 'center', width: '292px' }}>
              {upgradeButtonDescription || 'You can cancel any time.'}
            </P3>
          )}
        </BillingAdminAccess>
      </UpgradeNowContainer>
    </ModalContentWrapper>
  );
};

export const defaultProps = {
  contentType: 'highResVectorDownload',
  imageComponentType: 'none',
  designPreview: '',
} as const;

const UpgradeModalIncentiveContent: React.FC<
  UpgradeModalIncentiveContentProps
> = ({
  contentType = defaultProps.contentType,
  imageComponentType = defaultProps.imageComponentType,
  user,
  designPreview = defaultProps.designPreview,
  onUpgrade,
  onSecondaryButtonClick,
}) => {
  const ImageComponent = imageComponents[imageComponentType];
  const { isAvailable: isFreeTrialAvailable } = useFreeTrial();
  const { config } = useSharedConfigStore();
  const [data, setData] = useState<UpgradeModalConfigData | null>(null);

  const builderData: UpgradeModalIncentiveContentBuilderProps = {
    hideImageOnMobile: true,
    onUpgrade,
    background: getBackground(imageComponentType),
    imageComponent: <ImageComponent designPreview={designPreview} />,
    imageComponentType,
    dataTestId: 'upgrade-modal',
  };

  useEffect(() => {
    getWorkspaceMe()
      .then((workspace) => (workspace.success ? workspace.workspace : null))
      .catch(() => null)
      .then((workspace) => {
        const data = getConfig(contentType, user, config, workspace);
        if (data) {
          setData(data);
        }
      });
  }, [contentType, user, config]);

  if (data) {
    builderData.title = data.title;
    builderData.tag = data.tag;
    builderData.items = data.items;
    builderData.content = data.content;

    builderData.description = data.description;

    if (data.buttonLabel) {
      builderData.buttonLabel = data.buttonLabel;
    }

    if (data.upgradeButtonDescription) {
      builderData.upgradeButtonDescription = data.upgradeButtonDescription;
    }

    if (data.hideButtonDescription) {
      builderData.hideButtonDescription = data.hideButtonDescription;
    }

    if (data.itemsTitle) {
      builderData.itemsTitle = data.itemsTitle;
    }

    if (data.secondaryButtonLabel) {
      builderData.secondaryButtonLabel = data.secondaryButtonLabel;
      builderData.onSecondaryButtonClick = onSecondaryButtonClick;
    }

    if (data.onlyExpert) {
      builderData.onlyExpert = data.onlyExpert;
    }

    if (data.headerText) {
      builderData.headerText = data.headerText;
    }
  }

  return (
    <UpgradeModalIncentiveContentBuilder
      {...getModifiedConfigData(
        builderData,
        isFreeTrialAvailable,
        contentType,
        isMobileOperatingSystem()
      )}
    />
  );
};

export default UpgradeModalIncentiveContent;
